// src/pages/admin/AdminLiveMonitor.jsx
import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import {
    FaTv, FaExpandArrowsAlt, FaCompressArrowsAlt, FaClock,
    FaBullhorn, FaCalendarDay, FaTrophy, FaUserNinja,
    FaChevronLeft, FaChevronRight
} from 'react-icons/fa';
import api from '../../api/axios';
import { toast } from 'react-toastify';

// ======================================================================
// HELPER DATA
// ======================================================================
const normalizeText = (value) => String(value || '').toLowerCase().trim();

const getEntryMembers = (athlete) => {
    if (!athlete) return [];

    if (Array.isArray(athlete.entry_members)) {
        return athlete.entry_members
            .map(item => String(item || '').trim())
            .filter(Boolean);
    }

    if (typeof athlete.entry_members === 'string' && athlete.entry_members.trim() !== '') {
        try {
            const parsed = JSON.parse(athlete.entry_members);

            if (Array.isArray(parsed)) {
                return parsed
                    .map(item => String(item || '').trim())
                    .filter(Boolean);
            }
        } catch (error) {
            return athlete.entry_members
                .split(/\s*(?:\/|\||,|\+|&|\bdan\b|\n)\s*/i)
                .map(item => String(item || '').trim())
                .filter(Boolean);
        }
    }

    return [];
};

const getDisplayName = (athlete) => {
    const members = getEntryMembers(athlete);

    if (members.length > 1) {
        return members.join(' / ');
    }

    return athlete?.name || 'TBA';
};

const getTeamName = (athlete) => {
    return athlete?.team?.name || athlete?.Team?.name || athlete?.team_name || athlete?.teamName || '-';
};

const getAthleteBelt = (athlete) => {
    return athlete?.belt || '-';
};

const getMatchNumber = (match) => {
    return match?.match_num || match?.match_no || match?.draw_number || '-';
};

const getPoomsaeCategory = (match) => {
    return match?.poomsae_category || match?.category || 'Poomsae';
};

const getArenaSortValue = (arenaKey) => {
    if (arenaKey === 'POOMSAE') return 999;
    const number = String(arenaKey).match(/\d+/)?.[0];
    return number ? Number(number) : String(arenaKey).charCodeAt(0);
};

const AdminLiveMonitor = ({ type: propType }) => {
    // ======================================================================
    // ROUTING & STATE
    // ======================================================================
    const { type: paramType } = useParams();

    const activeType = propType || paramType || 'Prestasi';
    const currentType = activeType.charAt(0).toUpperCase() + activeType.slice(1);
    const isFestival = currentType === 'Festival';

    const [arenas, setArenas] = useState({});
    const [activeDay, setActiveDay] = useState(1);
    const [isFullscreen, setIsFullscreen] = useState(false);
    const [isLoading, setIsLoading] = useState(true);

    // State preview / navigasi partai per arena
    const [previewIndices, setPreviewIndices] = useState({});

    // Slider / pagination arena
    const [currentPage, setCurrentPage] = useState(0);
    const itemsPerPage = 4;

    const monitorRef = useRef(null);
    const availableDays = [1, 2, 3, 4, 5];

    // ======================================================================
    // FETCH DATA
    // ======================================================================
    const fetchLiveMonitor = async () => {
        try {
            const response = await api.get('/matches/caller/fetch', {
                params: {
                    day: activeDay,
                    type: currentType
                }
            });

            setArenas(response.data.data || {});
        } catch (err) {
            console.error("Gagal memuat data live monitor:", err);
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        setIsLoading(true);
        fetchLiveMonitor();

        const interval = setInterval(fetchLiveMonitor, 10000);

        return () => clearInterval(interval);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeDay, currentType]);

    useEffect(() => {
        setCurrentPage(0);
        setPreviewIndices({});
    }, [activeDay, currentType]);

    // ======================================================================
    // ARENA GROUPING & SLIDER
    // ======================================================================
    const allArenaKeys = useMemo(() => {
        const keys = Object.keys(arenas || {})
            .filter(key => key !== 'POOMSAE')
            .sort((a, b) => getArenaSortValue(a) - getArenaSortValue(b));

        if (arenas?.POOMSAE) keys.push('POOMSAE');

        return keys;
    }, [arenas]);

    const totalPages = Math.ceil(allArenaKeys.length / itemsPerPage);
    const visibleArenas = allArenaKeys.slice(
        currentPage * itemsPerPage,
        (currentPage + 1) * itemsPerPage
    );

    useEffect(() => {
        if (totalPages > 0 && currentPage >= totalPages) {
            setCurrentPage(0);
        }
    }, [totalPages, currentPage]);

    const handleNextPage = () => {
        if (totalPages <= 1) return;
        setCurrentPage(prev => (prev + 1) % totalPages);
    };

    const handlePrevPage = () => {
        if (totalPages <= 1) return;
        setCurrentPage(prev => (prev - 1 + totalPages) % totalPages);
    };

    useEffect(() => {
        let slideInterval;

        if (isFullscreen && totalPages > 1) {
            slideInterval = setInterval(() => {
                setCurrentPage(prev => (prev + 1) % totalPages);
            }, 10000);
        }

        return () => clearInterval(slideInterval);
    }, [isFullscreen, totalPages]);

    // ======================================================================
    // FULLSCREEN HANDLER
    // ======================================================================
    const toggleFullScreen = () => {
        if (!document.fullscreenElement) {
            monitorRef.current?.requestFullscreen().catch(() => {
                toast.error("Gagal mode layar penuh.");
            });
        } else {
            document.exitFullscreen();
        }
    };

    useEffect(() => {
        const handleFs = () => setIsFullscreen(!!document.fullscreenElement);

        document.addEventListener('fullscreenchange', handleFs);

        return () => document.removeEventListener('fullscreenchange', handleFs);
    }, []);

    // ======================================================================
    // PREVIEW NAVIGATION
    // ======================================================================
    const handleNextPreview = (arenaName, maxIndex) => {
        setPreviewIndices(prev => {
            const currentActual = arenas[arenaName]?.current_index || 0;
            const currentView = prev[arenaName] !== undefined ? prev[arenaName] : currentActual;

            if (currentView < maxIndex) {
                return { ...prev, [arenaName]: currentView + 1 };
            }

            return prev;
        });
    };

    const handlePrevPreview = (arenaName) => {
        setPreviewIndices(prev => {
            const currentActual = arenas[arenaName]?.current_index || 0;
            const currentView = prev[arenaName] !== undefined ? prev[arenaName] : currentActual;

            if (currentView > 0) {
                return { ...prev, [arenaName]: currentView - 1 };
            }

            return prev;
        });
    };

    const handleSyncActive = (arenaName) => {
        setPreviewIndices(prev => {
            const newIndices = { ...prev };
            delete newIndices[arenaName];
            return newIndices;
        });
    };

    // ======================================================================
    // TEXT TO SPEECH
    // ======================================================================
    const handleAnnounce = (match, arenaName, isPoomsae = false) => {
        if (!('speechSynthesis' in window)) {
            return toast.warning("Browser Anda tidak mendukung fitur panggilan suara.");
        }

        window.speechSynthesis.cancel();

        let textToSpeak = "";

        if (isPoomsae) {
            const athlete = match?.BlueAthlete;
            const athleteName = getDisplayName(athlete);
            const teamName = getTeamName(athlete);
            const category = getPoomsaeCategory(match);
            const matchNumber = getMatchNumber(match);
            const belt = match?.belt || getAthleteBelt(athlete);
            const sabukText = isFestival && belt !== '-' ? `Sabuk ${belt}, ` : '';

            textToSpeak = `Panggilan Poomsae ${currentType}, kategori ${category}, ${sabukText}undian nomor, ${matchNumber}. ${athleteName}, dari, ${teamName}. Silakan menuju ke arena Poomsae.`;
        } else {
            const blueName = getDisplayName(match?.BlueAthlete);
            const blueTeam = getTeamName(match?.BlueAthlete);
            const blueBelt = getAthleteBelt(match?.BlueAthlete);

            const redName = getDisplayName(match?.RedAthlete);
            const redTeam = getTeamName(match?.RedAthlete);
            const redBelt = getAthleteBelt(match?.RedAthlete);

            const matchNumber = getMatchNumber(match);

            const blueBeltText = isFestival && blueBelt !== '-' ? `, sabuk ${blueBelt}` : '';
            const redBeltText = isFestival && redBelt !== '-' ? `, sabuk ${redBelt}` : '';

            textToSpeak = `Panggilan pertandingan Kyorugi ${currentType}, partai nomor, ${matchNumber}. Sudut biru, ${blueName}${blueBeltText}, dari, ${blueTeam}. Melawan sudut merah, ${redName}${redBeltText}, dari, ${redTeam}. Silakan menuju ke lapangan, ${arenaName}.`;
        }

        toast.info(`🔊 Memanggil Arena ${arenaName}...`);

        const utterance = new SpeechSynthesisUtterance(textToSpeak);
        utterance.lang = 'id-ID';
        utterance.rate = 0.85;

        window.speechSynthesis.speak(utterance);
    };

    // ======================================================================
    // RENDER KYORUGI ARENA
    // ======================================================================
    const renderKyorugiArena = (arenaName, data) => {
        const actualIndex = data?.current_index || 0;
        const viewIndex = previewIndices[arenaName] !== undefined ? previewIndices[arenaName] : actualIndex;
        const displayedMatch = data?.all_matches ? data.all_matches[viewIndex] : null;
        const isLive = viewIndex === actualIndex;
        const maxIndex = data?.all_matches ? data.all_matches.length - 1 : 0;

        const upcomingMatchesList = data?.all_matches
            ? data.all_matches.slice(viewIndex + 1, viewIndex + 5)
            : [];

        if (!displayedMatch) return null;

        const matchNum = getMatchNumber(displayedMatch);

        const divisionText = isFestival
            ? displayedMatch.weight_class || 'Kyorugi Festival'
            : `${displayedMatch.division || '-'} - ${displayedMatch.weight_class || '-'}`;

        const blueName = getDisplayName(displayedMatch.BlueAthlete);
        const blueTeam = getTeamName(displayedMatch.BlueAthlete);
        const blueBelt = getAthleteBelt(displayedMatch.BlueAthlete);

        const redName = displayedMatch.is_bye
            ? 'BYE (KOSONG)'
            : getDisplayName(displayedMatch.RedAthlete);
        const redTeam = displayedMatch.is_bye
            ? 'OTOMATIS LOLOS'
            : getTeamName(displayedMatch.RedAthlete);
        const redBelt = getAthleteBelt(displayedMatch.RedAthlete);

        return (
            <div className="col-12 col-xl-6 mb-3" key={`kyorugi-${arenaName}`}>
                <div className="card border-0 shadow-sm rounded-4 overflow-hidden monitor-card h-100 d-flex flex-column bg-light" style={{ minHeight: '340px' }}>

                    {/* HEADER */}
                    <div className="p-3 d-flex justify-content-between align-items-center" style={{ backgroundColor: '#111827', color: 'white' }}>
                        <div className="d-flex align-items-center gap-3 overflow-hidden">
                            <div
                                className="rounded-3 d-flex justify-content-center align-items-center fw-black shadow-sm flex-shrink-0"
                                style={{
                                    backgroundColor: '#6366f1',
                                    width: '50px',
                                    height: '50px',
                                    fontSize: '1.5rem'
                                }}
                            >
                                {arenaName}
                            </div>

                            <div className="overflow-hidden">
                                <h5 className="m-0 fw-black text-uppercase tracking-wider text-truncate">
                                    LAPANGAN {arenaName}
                                </h5>
                                <div className="text-white-50 fw-bold text-uppercase mt-1 text-truncate" style={{ fontSize: '0.7rem' }}>
                                    {divisionText}
                                </div>
                            </div>
                        </div>

                        <div className="text-end flex-shrink-0">
                            <div
                                className="fw-black mb-1 tracking-widest text-uppercase"
                                style={{
                                    fontSize: '0.65rem',
                                    color: isLive ? '#94a3b8' : '#38bdf8'
                                }}
                            >
                                {isLive ? 'PARTAI SEKARANG' : 'SEDANG MELIHAT'}
                            </div>
                            <h2 className="m-0 fw-black" style={{ color: '#facc15' }}>
                                {matchNum}
                            </h2>
                        </div>
                    </div>

                    {/* BODY */}
                    <div className="d-flex flex-column flex-md-row flex-grow-1 p-3 gap-3">

                        {/* LEFT COLUMN */}
                        <div className="d-flex flex-column position-relative" style={{ flex: '1.4' }}>
                            {/* BLUE */}
                            <div
                                className="card border-2 rounded-4 text-center p-3 flex-grow-1 d-flex flex-column justify-content-center align-items-center position-relative"
                                style={{
                                    backgroundColor: '#f0f9ff',
                                    borderColor: '#bfdbfe'
                                }}
                            >
                                {displayedMatch.winner_id === displayedMatch.athlete_blue_id && (
                                    <div className="position-absolute top-0 start-0 m-2 badge bg-success fs-7 rounded-pill px-3 py-1 shadow-sm">
                                        WINNER
                                    </div>
                                )}

                                <div
                                    className="rounded-circle d-flex justify-content-center align-items-center text-white mb-2 shadow-sm"
                                    style={{
                                        width: '60px',
                                        height: '60px',
                                        backgroundColor: '#2563eb'
                                    }}
                                >
                                    <FaUserNinja size={30} />
                                </div>

                                <h5 className="fw-black text-dark text-uppercase mb-1 monitor-athlete-name">
                                    {blueName}
                                </h5>

                                <div className="fw-bold text-truncate w-100" style={{ color: '#2563eb', fontSize: '0.8rem' }}>
                                    {blueTeam}
                                </div>

                                {isFestival && (
                                    <div className="mt-2 badge bg-white text-primary border border-primary px-3 py-1 shadow-sm">
                                        Sabuk: {blueBelt}
                                    </div>
                                )}
                            </div>

                            {/* VS */}
                            <div className="position-absolute start-50 translate-middle z-3" style={{ top: '50%' }}>
                                <div
                                    className="badge rounded-pill shadow-sm d-flex justify-content-center align-items-center fw-black fst-italic"
                                    style={{
                                        backgroundColor: '#1e293b',
                                        color: 'white',
                                        border: '3px solid #f8f9fa',
                                        width: '45px',
                                        height: '35px',
                                        fontSize: '0.85rem'
                                    }}
                                >
                                    VS
                                </div>
                            </div>

                            {/* RED */}
                            <div
                                className="card border-2 rounded-4 text-center p-3 flex-grow-1 d-flex flex-column justify-content-center align-items-center mt-2 position-relative"
                                style={{
                                    backgroundColor: '#fef2f2',
                                    borderColor: '#fecaca'
                                }}
                            >
                                {displayedMatch.winner_id === displayedMatch.athlete_red_id && (
                                    <div className="position-absolute top-0 start-0 m-2 badge bg-danger fs-7 rounded-pill px-3 py-1 shadow-sm">
                                        WINNER
                                    </div>
                                )}

                                <div
                                    className="rounded-circle d-flex justify-content-center align-items-center text-white mb-2 shadow-sm"
                                    style={{
                                        width: '60px',
                                        height: '60px',
                                        backgroundColor: '#dc2626'
                                    }}
                                >
                                    <FaUserNinja size={30} />
                                </div>

                                <h5 className="fw-black text-dark text-uppercase mb-1 monitor-athlete-name">
                                    {redName}
                                </h5>

                                <div className="fw-bold text-truncate w-100" style={{ color: '#dc2626', fontSize: '0.8rem' }}>
                                    {redTeam}
                                </div>

                                {isFestival && !displayedMatch.is_bye && (
                                    <div className="mt-2 badge bg-white text-danger border border-danger px-3 py-1 shadow-sm">
                                        Sabuk: {redBelt}
                                    </div>
                                )}
                            </div>

                            <div className="text-center mt-2 hide-on-fullscreen">
                                <button
                                    onClick={() => handleAnnounce(displayedMatch, arenaName, false)}
                                    className="btn btn-sm btn-dark rounded-pill px-4 fw-bold shadow-sm hover-scale w-100"
                                    style={{ fontSize: '0.75rem' }}
                                >
                                    <FaBullhorn className="me-2 text-warning" /> Panggil Partai
                                </button>
                            </div>
                        </div>

                        {/* RIGHT COLUMN */}
                        <div className="d-flex flex-column bg-white rounded-4 border p-2" style={{ flex: '1', minWidth: '220px' }}>
                            <div className="text-center border-bottom pb-2 mb-2">
                                <div
                                    className="fw-black text-secondary tracking-widest d-flex justify-content-center align-items-center gap-2"
                                    style={{ fontSize: '0.65rem' }}
                                >
                                    <FaChevronRight size={10} /> PARTAI BERIKUTNYA
                                </div>

                                <div className="d-flex justify-content-between align-items-center gap-1 mt-2 bg-light rounded-pill p-1 border">
                                    <button
                                        className="btn btn-sm btn-white rounded-circle shadow-sm text-dark fw-bold px-2 py-1"
                                        onClick={() => handlePrevPreview(arenaName)}
                                        disabled={viewIndex <= 0}
                                    >
                                        &lt;
                                    </button>

                                    <button
                                        className={`btn btn-sm flex-grow-1 rounded-pill fw-bold text-uppercase shadow-sm ${isLive ? 'btn-primary text-white border-0' : 'btn-warning text-white border-0'}`}
                                        style={{ fontSize: '0.65rem' }}
                                        onClick={() => handleSyncActive(arenaName)}
                                    >
                                        {isLive ? 'SYNC KE ACTIVE' : 'KEMBALI KE LIVE'}
                                    </button>

                                    <button
                                        className="btn btn-sm btn-white rounded-circle shadow-sm text-dark fw-bold px-2 py-1"
                                        onClick={() => handleNextPreview(arenaName, maxIndex)}
                                        disabled={viewIndex >= maxIndex}
                                    >
                                        &gt;
                                    </button>
                                </div>
                            </div>

                            <div className="d-flex flex-column gap-2 overflow-auto custom-scrollbar flex-grow-1" style={{ height: '220px' }}>
                                {upcomingMatchesList.length > 0 ? upcomingMatchesList.map((m, idx) => (
                                    <div className="border rounded-3 p-2 bg-white shadow-sm" key={idx}>
                                        <div className="d-flex gap-2 align-items-center">
                                            <div className="text-center flex-shrink-0">
                                                <div className="fw-bold text-muted" style={{ fontSize: '0.5rem' }}>
                                                    PARTAI
                                                </div>
                                                <div className="fw-black text-dark" style={{ fontSize: '0.8rem' }}>
                                                    {getMatchNumber(m)}
                                                </div>
                                            </div>

                                            <div className="border-start ps-2 flex-grow-1 overflow-hidden">
                                                <div className="d-flex align-items-center gap-1 mb-1">
                                                    <span
                                                        className="rounded-circle flex-shrink-0"
                                                        style={{
                                                            width: '8px',
                                                            height: '8px',
                                                            backgroundColor: '#3b82f6'
                                                        }}
                                                    ></span>
                                                    <span className="fw-bold text-dark text-truncate" style={{ fontSize: '0.65rem' }}>
                                                        {getDisplayName(m.BlueAthlete)}
                                                    </span>
                                                </div>

                                                <div className="d-flex align-items-center gap-1">
                                                    <span
                                                        className="rounded-circle flex-shrink-0"
                                                        style={{
                                                            width: '8px',
                                                            height: '8px',
                                                            backgroundColor: '#ef4444'
                                                        }}
                                                    ></span>
                                                    <span className="fw-bold text-dark text-truncate" style={{ fontSize: '0.65rem' }}>
                                                        {m.is_bye ? 'BYE' : getDisplayName(m.RedAthlete)}
                                                    </span>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                )) : (
                                    <div className="text-center text-muted small py-4 fst-italic" style={{ fontSize: '0.7rem' }}>
                                        Habis / Kosong.
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    // ======================================================================
    // RENDER POOMSAE ARENA
    // ======================================================================
    const renderPoomsaeArena = (data) => {
        const actualIndex = data?.current_index || 0;
        const viewIndex = previewIndices.POOMSAE !== undefined ? previewIndices.POOMSAE : actualIndex;
        const displayedMatch = data?.all_matches ? data.all_matches[viewIndex] : null;
        const isLive = viewIndex === actualIndex;
        const maxIndex = data?.all_matches ? data.all_matches.length - 1 : 0;

        const upcomingMatchesList = data?.all_matches
            ? data.all_matches.slice(viewIndex + 1, viewIndex + 5)
            : [];

        if (!displayedMatch) return null;

        const athlete = displayedMatch.BlueAthlete;
        const displayName = getDisplayName(athlete);
        const teamName = getTeamName(athlete);
        const category = getPoomsaeCategory(displayedMatch);
        const belt = displayedMatch.belt || getAthleteBelt(athlete);
        const members = getEntryMembers(athlete);

        return (
            <div className="col-12 col-xl-6 mb-3" key="poomsae-arena">
                <div className="card border-0 shadow-sm rounded-4 overflow-hidden monitor-card h-100 d-flex flex-column bg-light" style={{ minHeight: '340px' }}>

                    <div className="p-3 d-flex justify-content-between align-items-center" style={{ backgroundColor: '#164e63', color: 'white' }}>
                        <div className="d-flex align-items-center gap-3 overflow-hidden">
                            <div
                                className="rounded-3 d-flex justify-content-center align-items-center fw-black shadow-sm bg-info text-dark flex-shrink-0"
                                style={{
                                    width: '50px',
                                    height: '50px',
                                    fontSize: '1.5rem'
                                }}
                            >
                                <FaTrophy />
                            </div>

                            <div className="overflow-hidden">
                                <h5 className="m-0 fw-black text-uppercase tracking-wider text-info text-truncate">
                                    ARENA POOMSAE
                                </h5>
                                <div className="text-white-50 fw-bold text-uppercase mt-1 text-truncate" style={{ fontSize: '0.7rem' }}>
                                    {displayedMatch.division || '-'} - {category}
                                </div>
                            </div>
                        </div>

                        <div className="text-end flex-shrink-0">
                            <div
                                className="fw-black mb-1 tracking-widest text-uppercase"
                                style={{
                                    fontSize: '0.65rem',
                                    color: isLive ? '#a5f3fc' : '#fde047'
                                }}
                            >
                                {isLive ? 'PESERTA SEKARANG' : 'SEDANG MELIHAT'}
                            </div>

                            <h2 className="m-0 fw-black text-white">
                                # {getMatchNumber(displayedMatch)}
                            </h2>
                        </div>
                    </div>

                    <div className="d-flex flex-column flex-md-row flex-grow-1 p-3 gap-3">

                        <div className="d-flex flex-column flex-grow-1" style={{ flex: '1.4' }}>
                            <div
                                className="card border-2 rounded-4 text-center p-4 flex-grow-1 d-flex flex-column justify-content-center align-items-center shadow-sm"
                                style={{
                                    backgroundColor: '#ecfeff',
                                    borderColor: '#a5f3fc'
                                }}
                            >
                                <div
                                    className="rounded-circle d-flex justify-content-center align-items-center text-white mb-3 shadow-sm"
                                    style={{
                                        width: '80px',
                                        height: '80px',
                                        backgroundColor: '#0891b2'
                                    }}
                                >
                                    <FaUserNinja size={40} />
                                </div>

                                <h4 className="fw-black text-dark text-uppercase mb-2 monitor-poomsae-name">
                                    {displayName}
                                </h4>

                                <h6 className="fw-bold m-0 text-truncate w-100" style={{ color: '#0891b2' }}>
                                    {teamName}
                                </h6>

                                <div className="mt-3 d-flex justify-content-center gap-2 flex-wrap">
                                    <div className="badge bg-white text-dark border px-3 py-1 shadow-sm" style={{ fontSize: '0.7rem' }}>
                                        {category}
                                    </div>

                                    {members.length > 1 && (
                                        <div className="badge bg-info bg-opacity-10 text-info border border-info px-3 py-1 shadow-sm" style={{ fontSize: '0.7rem' }}>
                                            {members.length} Nama / 1 Entry
                                        </div>
                                    )}

                                    {isFestival && belt !== '-' && (
                                        <div className="badge bg-white text-dark border px-3 py-1 shadow-sm" style={{ fontSize: '0.7rem' }}>
                                            Sabuk: {belt}
                                        </div>
                                    )}
                                </div>
                            </div>

                            <div className="text-center mt-2 hide-on-fullscreen">
                                <button
                                    onClick={() => handleAnnounce(displayedMatch, 'Poomsae', true)}
                                    className="btn btn-sm btn-white text-dark rounded-pill px-4 fw-bold shadow-sm hover-scale w-100"
                                    style={{ fontSize: '0.75rem' }}
                                >
                                    <FaBullhorn className="me-2" /> Panggil Peserta
                                </button>
                            </div>
                        </div>

                        <div className="d-flex flex-column bg-white rounded-4 border p-2" style={{ flex: '1', minWidth: '220px' }}>
                            <div className="text-center border-bottom pb-2 mb-2">
                                <div
                                    className="fw-black text-secondary tracking-widest d-flex justify-content-center align-items-center gap-2"
                                    style={{ fontSize: '0.65rem' }}
                                >
                                    <FaChevronRight size={10} /> PESERTA BERIKUTNYA
                                </div>

                                <div className="d-flex justify-content-between align-items-center gap-1 mt-2 bg-light rounded-pill p-1 border">
                                    <button
                                        className="btn btn-sm btn-white rounded-circle shadow-sm text-dark fw-bold px-2 py-1"
                                        onClick={() => handlePrevPreview('POOMSAE')}
                                        disabled={viewIndex <= 0}
                                    >
                                        &lt;
                                    </button>

                                    <button
                                        className={`btn btn-sm flex-grow-1 rounded-pill fw-bold text-uppercase shadow-sm ${isLive ? 'btn-info text-white border-0' : 'btn-warning text-dark border-0'}`}
                                        style={{ fontSize: '0.65rem' }}
                                        onClick={() => handleSyncActive('POOMSAE')}
                                    >
                                        {isLive ? 'SYNC KE ACTIVE' : 'KEMBALI KE LIVE'}
                                    </button>

                                    <button
                                        className="btn btn-sm btn-white rounded-circle shadow-sm text-dark fw-bold px-2 py-1"
                                        onClick={() => handleNextPreview('POOMSAE', maxIndex)}
                                        disabled={viewIndex >= maxIndex}
                                    >
                                        &gt;
                                    </button>
                                </div>
                            </div>

                            <div className="d-flex flex-column gap-2 overflow-auto custom-scrollbar flex-grow-1" style={{ height: '220px' }}>
                                {upcomingMatchesList.length > 0 ? upcomingMatchesList.map((m, idx) => (
                                    <div className="border rounded-3 p-2 bg-white shadow-sm d-flex align-items-center gap-2" key={idx}>
                                        <div className="badge bg-dark text-white px-2 py-1 flex-shrink-0" style={{ fontSize: '0.6rem' }}>
                                            {getMatchNumber(m)}
                                        </div>

                                        <div className="overflow-hidden">
                                            <div className="fw-black text-dark text-truncate" style={{ fontSize: '0.7rem' }}>
                                                {getDisplayName(m.BlueAthlete)}
                                            </div>
                                            <div className="text-muted text-truncate" style={{ fontSize: '0.55rem' }}>
                                                {m.division || '-'} • {getPoomsaeCategory(m)}
                                            </div>
                                        </div>
                                    </div>
                                )) : (
                                    <div className="text-center text-muted small py-4 fst-italic" style={{ fontSize: '0.7rem' }}>
                                        Habis / Kosong.
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    // ======================================================================
    // RENDER
    // ======================================================================
    return (
        <div
            ref={monitorRef}
            className="container-fluid p-3 p-md-4 min-vh-100 position-relative animate-fade-in"
            id="arena-display-container"
            style={{
                backgroundColor: '#e2e8f0'
            }}
        >

            {/* HEADER CONTROLS */}
            <div className="d-flex flex-column flex-md-row justify-content-between align-items-md-center mb-3 gap-3 hide-on-fullscreen">
                <div>
                    <h4 className="fw-black text-dark tracking-tight mb-1 d-flex align-items-center gap-2">
                        <FaTv className="text-primary" /> Live Monitor {currentType}
                    </h4>
                    <p className="text-muted m-0 small">
                        Bisa mengintip jadwal selanjutnya tanpa merusak sinkronisasi live partai saat ini.
                    </p>
                </div>

                <div className="d-flex flex-wrap align-items-center gap-2 bg-white p-2 rounded-pill shadow-sm border">
                    <div className="d-flex align-items-center gap-2 px-3 border-end">
                        <FaCalendarDay className="text-primary" />
                        <select
                            className="form-select form-select-sm border-0 fw-bold bg-transparent cursor-pointer"
                            value={activeDay}
                            onChange={(e) => setActiveDay(parseInt(e.target.value))}
                        >
                            {availableDays.map(day => (
                                <option key={day} value={day}>
                                    HARI KE-{day}
                                </option>
                            ))}
                        </select>
                    </div>

                    <div className="d-flex align-items-center gap-2 px-3 text-muted small fw-bold">
                        <FaClock /> {new Date().toLocaleTimeString('id-ID', { hour: '2-digit', minute: '2-digit' })} WIB
                    </div>

                    <button
                        onClick={toggleFullScreen}
                        className="btn btn-sm btn-dark rounded-pill px-4 fw-bold shadow-sm hover-scale d-flex align-items-center gap-2"
                    >
                        {isFullscreen ? (
                            <>
                                <FaCompressArrowsAlt /> Keluar
                            </>
                        ) : (
                            <>
                                <FaExpandArrowsAlt /> Layar Penuh
                            </>
                        )}
                    </button>
                </div>
            </div>

            {/* SLIDER CONTROLS */}
            {totalPages > 1 && (
                <>
                    <button
                        onClick={handlePrevPage}
                        className="btn btn-dark position-fixed top-50 start-0 translate-middle-y ms-2 z-3 rounded-circle shadow-lg floating-nav-btn d-flex align-items-center justify-content-center"
                    >
                        <FaChevronLeft />
                    </button>

                    <button
                        onClick={handleNextPage}
                        className="btn btn-dark position-fixed top-50 end-0 translate-middle-y me-2 z-3 rounded-circle shadow-lg floating-nav-btn d-flex align-items-center justify-content-center"
                    >
                        <FaChevronRight />
                    </button>

                    <div className="position-fixed bottom-0 start-50 translate-middle-x mb-2 z-3 d-flex gap-2 bg-dark bg-opacity-75 px-3 py-2 rounded-pill hide-on-normal">
                        {Array.from({ length: totalPages }).map((_, idx) => (
                            <span
                                key={idx}
                                className="rounded-circle bg-white"
                                style={{
                                    width: currentPage === idx ? '18px' : '8px',
                                    height: '8px',
                                    opacity: currentPage === idx ? 1 : 0.25,
                                    transition: 'all 0.3s'
                                }}
                            ></span>
                        ))}
                    </div>
                </>
            )}

            {/* CONTENT */}
            {isLoading && Object.keys(arenas).length === 0 ? (
                <div className="d-flex flex-column justify-content-center align-items-center py-5 my-5">
                    <div className="spinner-border text-primary mb-3" style={{ width: '3rem', height: '3rem' }}></div>
                    <h6 className="fw-bold text-muted">Menyinkronkan data arena...</h6>
                </div>
            ) : Object.keys(arenas).length === 0 ? (
                <div className="text-center py-5 my-5 border-dashed rounded-4 bg-white shadow-sm">
                    <FaTv size={50} className="text-muted opacity-25 mb-3" />
                    <h5 className="fw-black text-dark mb-1">Belum Ada Pertandingan</h5>
                    <p className="text-muted small">
                        Tidak ada jadwal pertandingan {currentType} pada Hari ke-{activeDay}.
                    </p>
                </div>
            ) : (
                <div className="row g-3">
                    {visibleArenas.map(arenaKey => {
                        if (arenaKey === 'POOMSAE') {
                            return renderPoomsaeArena(arenas[arenaKey]);
                        }

                        return renderKyorugiArena(arenaKey, arenas[arenaKey]);
                    })}
                </div>
            )}

            {/* STYLES */}
            <style>{`
                .animate-fade-in {
                    animation: fadeIn 0.4s ease-out forwards;
                }

                .hover-scale {
                    transition: transform 0.2s;
                }

                .hover-scale:hover {
                    transform: scale(1.05);
                }

                .cursor-pointer {
                    cursor: pointer;
                }

                .tracking-tight {
                    letter-spacing: -0.5px;
                }

                .tracking-wider {
                    letter-spacing: 1px;
                }

                .tracking-widest {
                    letter-spacing: 2px;
                }

                .custom-scrollbar::-webkit-scrollbar {
                    width: 4px;
                }

                .custom-scrollbar::-webkit-scrollbar-track {
                    background: transparent;
                }

                .custom-scrollbar::-webkit-scrollbar-thumb {
                    background: #dee2e6;
                    border-radius: 10px;
                }

                .custom-scrollbar::-webkit-scrollbar-thumb:hover {
                    background: #adb5bd;
                }

                .floating-nav-btn {
                    width: 45px;
                    height: 45px;
                    opacity: 0.3;
                    transition: all 0.3s ease;
                }

                .floating-nav-btn:hover {
                    opacity: 0.9;
                    transform: scale(1.1);
                }

                .monitor-athlete-name,
                .monitor-poomsae-name {
                    max-width: 100%;
                    display: -webkit-box;
                    -webkit-line-clamp: 2;
                    -webkit-box-orient: vertical;
                    overflow: hidden;
                    line-height: 1.2;
                    word-break: break-word;
                }

                .monitor-athlete-name {
                    font-size: 1.05rem;
                }

                .monitor-poomsae-name {
                    font-size: 1.35rem;
                }

                @media (max-width: 576px) {
                    .monitor-athlete-name {
                        font-size: 0.95rem;
                    }

                    .monitor-poomsae-name {
                        font-size: 1.05rem;
                    }
                }

                @keyframes fadeIn {
                    from {
                        opacity: 0;
                        transform: translateY(10px);
                    }
                    to {
                        opacity: 1;
                        transform: translateY(0);
                    }
                }

                .hide-on-normal {
                    display: none !important;
                }

                :fullscreen {
                    background-color: #e2e8f0;
                    padding: 20px;
                    overflow: hidden;
                }

                :fullscreen .hide-on-fullscreen {
                    display: none !important;
                }

                :fullscreen .hide-on-normal {
                    display: flex !important;
                }

                :fullscreen .monitor-card {
                    box-shadow: 0 10px 25px rgba(0, 0, 0, 0.2) !important;
                }

                :fullscreen .floating-nav-btn {
                    opacity: 0.1;
                }

                :fullscreen .floating-nav-btn:hover {
                    opacity: 0.8;
                }

                :-webkit-full-screen {
                    background-color: #e2e8f0;
                    padding: 20px;
                    overflow: hidden;
                }

                :-moz-full-screen {
                    background-color: #e2e8f0;
                    padding: 20px;
                    overflow: hidden;
                }

                :-ms-fullscreen {
                    background-color: #e2e8f0;
                    padding: 20px;
                    overflow: hidden;
                }
            `}</style>
        </div>
    );
};

export default AdminLiveMonitor;